import axios from "axios";
import { httpClient } from "@/shared/services/http";
import { PaginationResponse } from "@/shared/interfaces/pagination.interface";
import {
  AssessmentTemplate,
  CreateTemplatePayload,
  TemplateCreationResult,
} from "@/modules/assessments/types/template-creation.interface";
import isValidationFailureResponse from "@/shared/utils/comparison/isValidationFailureResponse";

export interface IAssessmentTemplateListItem {
  templateId: number;
  name: string;
  clientName: string | null;
  version: number;
  createdByName: string | null;
  createdAt: string;
}

export interface GetAssessmentTemplatesParameters {
  take?: number;
  skip?: number;
  search?: string;
}

export class AssessmentTemplateHttpService {
  static async getLatestActiveTemplates(clientId: number) {
    const { data: templates } = await httpClient.get(`/api/clients/${clientId}/assessment-templates`);
    return templates;
  }

  static async getAssessmentTemplates({ take, skip, search }: GetAssessmentTemplatesParameters = {}): Promise<
    PaginationResponse<IAssessmentTemplateListItem>
  > {
    const searchParams = new URLSearchParams();
    if (typeof take === "number" && !isNaN(take)) {
      searchParams.set("take", String(take));
    }

    if (typeof skip === "number" && !isNaN(skip)) {
      searchParams.set("skip", String(skip));
    }

    const searchQuery = search?.trim();
    if (searchQuery) {
      searchParams.set("search", searchQuery);
    }

    const searchTerms = searchParams.toString();
    const url = `/api/assessment-templates` + (searchTerms ? `?${searchTerms}` : "");
    const { data } = await httpClient.get<PaginationResponse<IAssessmentTemplateListItem>>(url);
    return data;
  }

  static async getTemplate(templateId: number): Promise<AssessmentTemplate> {
    const { data } = await httpClient.get<AssessmentTemplate>(`/api/assessment-templates/${templateId}`);
    return data;
  }

  static async createNewTemplate(payload: CreateTemplatePayload): Promise<TemplateCreationResult> {
    return this.postTemplateCreation("/api/assessment-templates", payload);
  }

  static async createTemplateVersion(
    sourceTemplateId: number,
    payload: CreateTemplatePayload,
  ): Promise<TemplateCreationResult> {
    return this.postTemplateCreation(`/api/assessment-templates/${sourceTemplateId}/create-version`, payload);
  }

  private static async postTemplateCreation(
    url: string,
    payload: CreateTemplatePayload,
  ): Promise<TemplateCreationResult> {
    const file = payload.zipFile?.originFileObj;
    if (!file) {
      throw new Error("ZIP file is required for template creation");
    }

    const formData = new FormData();
    formData.append("templateName", payload.templateName);
    formData.append("tests", JSON.stringify(payload.tests));
    formData.append("formulas", payload.formulas);
    formData.append("csvBundle", file);

    if (payload.clientId !== undefined) {
      formData.append("clientId", String(payload.clientId));
    }
    if (payload.landingPageText) {
      formData.append("landingPageText", JSON.stringify(payload.landingPageText));
    }
    if (payload.landingPageImage) {
      formData.append("landingPageImage", payload.landingPageImage);
    } else if (payload.landingPageImageUrl) {
      formData.append("landingPageImageUrl", payload.landingPageImageUrl);
    }

    try {
      const { data } = await httpClient.post<TemplateCreationResult>(url, formData);
      return data;
    } catch (error: unknown) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 400 &&
        isValidationFailureResponse(error.response.data)
      ) {
        return error.response.data;
      }
      throw error;
    }
  }
}
